<template>
  <p-form-element class="p-date-field">
    <p-text-field-formatted
      ref="textField"
      :label="innerLabel"
      :display-formatter="formatDisplay"
      :value-formatter="formatValue"
      :user-input-formatter="formatUserInput"
      :value="value"
      :required="innerRequired"
      :errors="innerErrors"
      :context-icon="contextIcon || (readonly || disabled ? 'today' : undefined)"
      :messages="innerMessages"
      :inputmode="unit === 'year' ? 'numeric' : undefined"
      :pattern="unit === 'year' ? '[0-9]*' : undefined"
      v-bind="$props"
      v-on="{
        ...$listeners,
      }"
    >
      <template #actions>
        <p-menu
          v-if="unit !== 'year'"
          v-model="datepickerVisible"
          :open-on-click="false"
        >
          <template slot="activator">
            <p-button v-if="!readonly && !disabled" ref="datePickerButton" dense tertiary @click="openDatePicker">
              <p-icon>today</p-icon>
            </p-button>
          </template>
          <p-card class="p-date-field-picker">
            <template v-if="unit === 'month'">
              <p-date-month-picker ref="datePicker" :min="min" :max="max" :value="value" @input="selectDateFromPicker"></p-date-month-picker>
            </template>
            <template v-else>
              <p-date-picker ref="datePicker" :min="min" :max="max" :value="value" @input="selectDateFromPicker"></p-date-picker>
            </template>
          </p-card>
        </p-menu>
      </template>
    </p-text-field-formatted>
  </p-form-element>
</template>

<script lang="ts">
  import Vue from 'vue';
  import { Dayjs } from 'dayjs';
  import PTextField from '../p-text-field/p-text-field.vue';
  import interactable from '../../mixins/interactable/interactable';
  import colorable from '../../mixins/colorable/colorable';
  import clearable from '../../mixins/clearable/clearable';
  import mixins from '../../mixins';
  import validatable from '../../mixins/validatable/validatable';

  export default mixins(interactable, colorable, clearable, validatable).extend({
    mixins: [validatable],
    props: {
      // TODO: This seems like it creates an entire instance just to read the props, that is uneccessary
      ...((new PTextField()).$options.props),
      // Override the props that need different values below
      value: {
        type: String,
        default: undefined,
        validator: (value) => Vue.$date(value).isValid(),
      },
      unit: {
        type: String,
        default: 'date',
        validator: (value) => {
          return value === 'date'
            || value === 'week'
            || value === 'month'
            || value === 'year';
        },
      },
      min: {
        type: String,
        default: undefined,
        validator: (value) => Vue.$date(value).isValid(),
      },
      max: {
        type: String,
        default: undefined,
        validator: (value) => Vue.$date(value).isValid(),
      },
      /** Either a format-string _"dd MM YY"_ or a function that returns the formatted string */
      displayFormatter: { type: [String, Function], default: undefined },
    },
    data: () => ({
      datepickerVisible: false,
      display: undefined as string | undefined,
      validDateFormats: [
        'D.M.YYYY',
        'DD.MM.YYYY',
        'DD.M.YYYY',
        'D.MM.YYYY',
        'D/M/YYYY',
        'DD/MM/YYYY',
        'D/MM/YYYY',
        'DD/M/YYYY',
        'D-M-YYYY',
        'D.M.YY',
        'DD.MM.YY',
        'DD.M.YY',
        'D.MM.YY',
        'D/M/YY',
        'DD/MM/YY',
        'D/MM/YY',
        'DD/M/YY',
        'D-M-YY',
        'DD-MM-YY',
        'D-MM-YY',
        'DD-M-YY',
        'DD-MM-YYYY',
        'D-MM-YYYY',
        'DD-M-YYYY',
        'YYYY-MM-DD',
        'YYYY',
        'YY',
        'M YYYY',
        'M. YYYY',
        'MM. YYYY',
        'MM YYYY',
        'MMM YYYY',
        'MMMM YYYY',
      ],
    }),
    methods: {
      openDatePicker() {
        if (this.disabled || this.readonly) {
          return;
        }
        this.datepickerVisible = true;
        // Takes a tick before it displays
        this.$nextTick(() => {
          const datePicker = this.$refs.datePicker as Vue;
          if (datePicker) {
            const el = datePicker.$el as HTMLElement;
            const dayPanels = el.getElementsByClassName('p-date-picker-days-panel');
            if (dayPanels.length > 0) {
              (dayPanels[0] as HTMLElement).focus();
            }
          }
        });
      },
      selectDateFromPicker(date: string) {
        this.datepickerVisible = false;
        const datePickerButton = this.$refs.datePickerButton as Vue;
        if (datePickerButton) {
          const el = datePickerButton.$el as HTMLElement;
          el?.focus();
        }
        this.$emit('input', this.formatValue(date));
      },
      formatToLocalDate(isoValue: string) {
        switch (this.unit) {
        case 'month':
          return this.$format.date(isoValue, 'MMM YYYY');
        case 'year':
          return this.$format.date(isoValue, 'YYYY');
        case 'date':
          return this.$format.date(isoValue, 'L');
        default:
          return this.$format.date(isoValue, 'L');
        }
      },
      formatDisplay(userInput: string | undefined, value: string | undefined) {
        if (typeof this.displayFormatter === 'function') {
          return this.displayFormatter(userInput, value);
        }
        if (value && typeof this.displayFormatter === 'string') {
          return this.$format.date(value, this.displayFormatter);
        }
        if (value) {
          const formattedLocal = this.formatToLocalDate(value);
          return formattedLocal;
        }
        return undefined;
      },
      formatUserInput(userInput: string | undefined, value: string | undefined, display: string | undefined) {
        const dateObj = this.$date(value);
        return this.formatToLocalDate(dateObj as any);
      },
      formatValue(stringDate: string | undefined) {
        if (stringDate === '' || stringDate === undefined) {
          return undefined;
        }
        let inputDate: Dayjs | undefined = this.$date(stringDate.trim(), this.validDateFormats, true);
        inputDate = this.validateDate(inputDate);
        inputDate = this.clampToUnit(inputDate);
        return inputDate?.format('YYYY-MM-DD');
      },
      validateDate(inputDate: Dayjs) {
        this.innerErrors = undefined;
        if (!inputDate || !inputDate.isValid()) {
          this.innerErrors = this.$t('core.validation.date');
          return undefined;
        }
        const isBeforeMin = this.min && inputDate.isBefore(this.min);
        const isAfterMax = this.max && inputDate.isAfter(this.max);
        if (isBeforeMin) {
          this.innerErrors = this.$t('core.validation.minimum', this.$format.localDate(this.min));
          return undefined;
        }
        if (isAfterMax) {
          this.innerErrors = this.$t('core.validation.maximum', this.$format.localDate(this.max));
          return undefined;
        }
        return inputDate;
      },
      clampToUnit(inputDate?: Dayjs) {
        switch (this.unit) {
        case 'year':
          return this.$date(inputDate?.format('YYYY-1-1'));
        case 'month':
          return this.$date(inputDate?.format('YYYY-MM-1'));
        case 'date':
          return this.$date(inputDate?.format('YYYY-MM-DD'));
        default:
          return inputDate;
        }
      },
    },
  });
</script>
